/**
 * /system-admin/users — user approval queue and lifecycle management (A6.2).
 *
 * Search params: status (filter), q (search), page (1-based).
 * Dashboard can deep-link ?status=pending_approval to pre-filter the queue.
 */
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import { useTranslations } from "use-intl";
import { Users } from "lucide-react";
import { PageHeader } from "@/components/ui/page-header";
import { STANDARD_PAGE_SIZE } from "@/components/ui/pagination";
import { SystemAdminGuard } from "@/features/system-admin/components/system-admin-guard";
import { useSystemUsers } from "@/features/system-admin/api/users";
import { UsersFilters } from "@/features/system-admin/components/users/users-filters";
import { UsersTable } from "@/features/system-admin/components/users/users-table";

const usersSearchSchema = z.object({
  status: z.string().optional().catch(undefined),
  q: z.string().optional().catch(undefined),
  page: z.number().int().positive().optional().catch(undefined),
  pageSize: z.number().int().positive().optional().catch(undefined),
});

export const Route = createFileRoute("/system-admin/users")({
  validateSearch: usersSearchSchema.parse,
  component: SystemAdminUsersPage,
});

function SystemAdminUsersPage() {
  const t = useTranslations("system-admin.users");
  const tc = useTranslations("system-admin.common");
  const navigate = useNavigate();
  const { status = "", q = "", page = 1, pageSize = STANDARD_PAGE_SIZE } = Route.useSearch();

  const filter = {
    status: status || undefined,
    q: q || undefined,
    page,
    pageSize,
  };

  const { data, isLoading, isFetching, refetch } = useSystemUsers(filter);

  const rows = data?.data ?? [];
  const total = data?.paging?.total ?? 0;

  function updateSearch(patch: { status?: string; q?: string; page?: number; pageSize?: number }) {
    void navigate({
      to: "/system-admin/users",
      search: {
        status: patch.status ?? status,
        q: patch.q ?? q,
        page: patch.page ?? 1,
        pageSize: patch.pageSize ?? pageSize,
      },
      replace: true,
    });
  }

  return (
    <SystemAdminGuard>
      <div className="space-y-6">
        <PageHeader
          icon={Users}
          title={tc("pages.users.title")}
          description={tc("pages.users.subtitle")}
          onReload={() => refetch()}
          isReloading={isFetching}
          totalLabel={total > 0 ? t("meta.found", { count: total }) : undefined}
        >
          <UsersFilters
            status={status}
            q={q}
            onStatusChange={(val) => updateSearch({ status: val, page: 1 })}
            onQChange={(val) => updateSearch({ q: val, page: 1 })}
          />
        </PageHeader>

        <UsersTable
          data={rows}
          total={total}
          isLoading={isLoading}
          page={page}
          pageSize={pageSize}
          onPageChange={(p) => updateSearch({ page: p })}
          onPageSizeChange={(size) => updateSearch({ page: 1, pageSize: size })}
        />
      </div>
    </SystemAdminGuard>
  );
}
